import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { userApi } from "../api/users";
import type {
  ArchiveUserPayload,
  AssignmentOption,
  AssignmentOptionsParams,
  CreateUserPayload,
  UpdateUserPayload,
  UserDirectoryParams,
} from "../api/users";
import { requireData, yieldToCancellation } from "./http";
import { invalidateDashboard } from "./dashboard";

export const userQueryKeys = {
  all: ["users"] as const,
  summary: (id: string) => ["users", "summary", id] as const,
  directory: (params: UserDirectoryParams) =>
    ["users", "directory", params] as const,
  detail: (id: string) => ["users", "detail", id] as const,
  dependencies: (id: string) => ["users", "dependencies", id] as const,
  assignmentOptions: (params: AssignmentOptionsParams) =>
    ["users", "assignment-options", params] as const,
};

export const userDirectoryQueryOptions = (params: UserDirectoryParams) =>
  queryOptions({
    queryKey: userQueryKeys.directory(params),
    queryFn: async ({ signal }) => {
      await yieldToCancellation(signal);
      return requireData(await userApi.directory(params, signal));
    },
    staleTime: 30 * 1000,
  });

export const useUserDirectory = (params: UserDirectoryParams) =>
  useQuery({
    ...userDirectoryQueryOptions(params),
    placeholderData: keepPreviousData,
  });

export const userDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.detail(id),
    queryFn: async ({ signal }) => requireData(await userApi.get(id, signal)),
    enabled: Boolean(id),
  });

export const useUserDetail = (id: string, enabled = true) =>
  useQuery({ ...userDetailQueryOptions(id), enabled: enabled && Boolean(id) });

export const userDependenciesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.dependencies(id),
    queryFn: async ({ signal }) =>
      requireData(await userApi.dependencies(id, signal)),
    enabled: Boolean(id),
  });

export const useUserDependencies = (id: string, enabled = true) =>
  useQuery({
    ...userDependenciesQueryOptions(id),
    enabled: enabled && Boolean(id),
  });

export const assignmentOptionsQueryOptions = (
  params: AssignmentOptionsParams,
  queryClient?: QueryClient,
) =>
  queryOptions({
    queryKey: userQueryKeys.assignmentOptions(params),
    queryFn: async ({ signal }) => {
      const page = requireData(await userApi.assignmentOptions(params, signal));
      page.items.forEach((user) =>
        queryClient?.setQueryData<AssignmentOption>(
          userQueryKeys.summary(user.id),
          user,
        ),
      );
      return page;
    },
    staleTime: 30 * 1000,
  });

export const useAssignmentOptions = (
  params: AssignmentOptionsParams,
  { enabled = true, keepPrevious = false } = {},
) => {
  const queryClient = useQueryClient();
  return useQuery({
    ...assignmentOptionsQueryOptions(params, queryClient),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
};

type CandidateKind = AssignmentOptionsParams["kind"];
/** Where each kind left off. `null` means the first page of every kind. */
type CandidateCursors = Partial<Record<CandidateKind, string>> | null;

const CANDIDATE_KINDS: CandidateKind[] = ["customer", "agent"];
const CANDIDATE_PAGE_SIZE = 20;

/**
 * People staff can add to a conversation, clients and colleagues together.
 *
 * The API pages each kind on its own cursor, so a page here is one page of each
 * kind that still has more. Keyed under `users`, so a change to a user
 * refreshes it with the rest of the user cache.
 */
export const useChatCandidates = (
  search: string,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: ["users", "chat-candidates", search] as const,
    initialPageParam: null as CandidateCursors,
    queryFn: async ({ signal, pageParam }) => {
      await yieldToCancellation(signal);
      const kinds = pageParam
        ? CANDIDATE_KINDS.filter((kind) => pageParam[kind])
        : CANDIDATE_KINDS;
      const pages = await Promise.all(
        kinds.map(async (kind) => ({
          kind,
          page: requireData(
            await userApi.assignmentOptions(
              {
                kind,
                search,
                limit: CANDIDATE_PAGE_SIZE,
                cursor: pageParam?.[kind],
              },
              signal,
            ),
          ),
        })),
      );
      const next: Partial<Record<CandidateKind, string>> = {};
      for (const { kind, page } of pages) {
        if (page.next_cursor) next[kind] = page.next_cursor;
      }
      return { items: pages.flatMap(({ page }) => page.items), next };
    },
    getNextPageParam: (last): CandidateCursors | undefined =>
      Object.keys(last.next).length ? last.next : undefined,
    enabled,
    staleTime: 30 * 1000,
  });

export const invalidateUserQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: userQueryKeys.all }),
    invalidateDashboard(queryClient),
  ]);

const useInvalidatingUserMutation = <TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => invalidateUserQueries(queryClient),
  });
};

export const useCreateUser = () =>
  useInvalidatingUserMutation((payload: CreateUserPayload) =>
    userApi.create(payload).then(requireData),
  );

export const useUpdateUser = () =>
  useInvalidatingUserMutation(
    ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
      userApi.update(id, payload).then(requireData),
  );

export const useArchiveUser = () =>
  useInvalidatingUserMutation(
    ({ id, payload }: { id: string; payload: ArchiveUserPayload }) =>
      userApi.archive(id, payload).then(requireData),
  );

export const useRestoreUser = () =>
  useInvalidatingUserMutation(
    ({ id, expectedVersion }: { id: string; expectedVersion: number }) =>
      userApi.restore(id, expectedVersion).then(requireData),
  );

export const usePermanentlyDeleteUser = () =>
  useInvalidatingUserMutation(
    ({ id, expectedVersion }: { id: string; expectedVersion: number }) =>
      userApi.permanentlyDelete(id, expectedVersion).then(requireData),
  );

export const useResendInvitation = () =>
  useInvalidatingUserMutation((id: string) =>
    userApi.resendInvitation(id).then(requireData),
  );
